import React, { useEffect, useState } from 'react';
import { api } from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ShoppingCart, Plus, Minus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const Sales = () => {
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [cart, setCart] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerName, setCustomerName] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('credito');
  const [saleType, setSaleType] = useState('individual');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [productsRes, customersRes] = await Promise.all([
        api.products.getAll(),
        api.customers.getAll()
      ]);
      setProducts(productsRes.data);
      setCustomers(customersRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    }
  };

  const addToCart = (product) => {
    const existing = cart.find(item => item.product_id === product.id);
    if (existing) {
      if (existing.quantity >= product.stock) {
        toast.error('Estoque insuficiente');
        return;
      }
      setCart(cart.map(item => 
        item.product_id === product.id 
          ? { ...item, quantity: item.quantity + 1, total: (item.quantity + 1) * item.unit_price }
          : item
      ));
    } else {
      setCart([...cart, {
        product_id: product.id,
        product_name: product.name,
        quantity: 1,
        unit_price: product.price,
        total: product.price
      }]);
    }
  };

  const updateQuantity = (productId, change) => {
    const product = products.find(p => p.id === productId);
    setCart(cart.map(item => {
      if (item.product_id === productId) {
        const newQuantity = item.quantity + change;
        if (newQuantity <= 0) return item;
        if (newQuantity > product.stock) {
          toast.error('Estoque insuficiente');
          return item;
        }
        return { ...item, quantity: newQuantity, total: newQuantity * item.unit_price };
      }
      return item;
    }));
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item.product_id !== productId));
  };

  const calculateTotal = () => {
    return cart.reduce((sum, item) => sum + item.total, 0);
  };

  const handleCompleteSale = async () => {
    if (cart.length === 0) {
      toast.error('Adicione produtos ao carrinho');
      return;
    }

    const finalCustomerName = selectedCustomer ? customers.find(c => c.id === selectedCustomer)?.name : customerName;
    if (!finalCustomerName) {
      toast.error('Informe o nome do cliente');
      return;
    }

    setLoading(true);
    try {
      const saleData = {
        customer_id: selectedCustomer || null,
        customer_name: finalCustomerName,
        items: cart,
        total: calculateTotal(), // A PEÇA QUE FALTAVA
        payment_method: paymentMethod,
        status: paymentMethod === 'credito' ? 'pendente' : 'pago',
        sale_type: saleType
      };

      await api.sales.create(saleData);
      toast.success('Venda realizada com sucesso!');
      
      setCart([]);
      setSelectedCustomer(null);
      setCustomerName('');
      setPaymentMethod('credito');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao processar venda');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="sales-page">
      <h1 className="text-5xl font-bold uppercase tracking-tight">Nova Venda</h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="bg-card border-border" data-testid="products-grid">
            <CardHeader>
              <CardTitle className="text-xl uppercase tracking-wide">Produtos</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {products.map(product => (
                  <button
                    key={product.id}
                    onClick={() => addToCart(product)}
                    disabled={product.stock === 0}
                    className={`p-4 border-2 transition-all text-left ${
                      product.stock === 0 
                        ? 'border-border bg-muted/20 opacity-50 cursor-not-allowed' 
                        : 'border-border hover:border-primary hover:bg-primary/5 cursor-pointer'
                    }`}
                    data-testid={`product-card-${product.id}`}
                  >
                    <p className="font-bold text-foreground truncate">{product.name}</p>
                    <p className="text-primary font-mono font-bold text-lg">R$ {product.price.toFixed(2)}</p>
                    <p className="text-xs text-muted-foreground font-mono">Estoque: {product.stock}</p>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card className="bg-card border-2 border-primary" data-testid="cart-card">
            <CardHeader>
              <div className="flex items-center gap-2">
                <ShoppingCart className="h-5 w-5 text-primary" />
                <CardTitle className="text-xl uppercase tracking-wide">Carrinho</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {cart.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">Carrinho vazio</p>
              ) : (
                <div className="space-y-3">
                  {cart.map(item => (
                    <div key={item.product_id} className="p-3 bg-secondary border border-border" data-testid={`cart-item-${item.product_id}`}>
                      <div className="flex justify-between items-start mb-2">
                        <p className="font-medium text-sm">{item.product_name}</p>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 hover:bg-destructive/20 hover:text-destructive"
                          onClick={() => removeFromCart(item.product_id)}
                          data-testid={`remove-cart-item-${item.product_id}`}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-6 w-6 border-border"
                            onClick={() => updateQuantity(item.product_id, -1)}
                            data-testid={`decrease-quantity-${item.product_id}`}
                          >
                            <Minus className="h-3 w-3" />
                          </Button>
                          <span className="font-mono font-bold w-8 text-center">{item.quantity}</span>
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-6 w-6 border-border"
                            onClick={() => updateQuantity(item.product_id, 1)}
                            data-testid={`increase-quantity-${item.product_id}`}
                          >
                            <Plus className="h-3 w-3" />
                          </Button>
                        </div>
                        <p className="font-mono font-bold text-primary">R$ {item.total.toFixed(2)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="pt-4 border-t-2 border-primary">
                <div className="flex justify-between items-center mb-6">
                  <p className="text-xl font-bold uppercase">Total</p>
                  <p className="text-2xl font-mono font-bold text-primary" data-testid="cart-total">R$ {calculateTotal().toFixed(2)}</p>
                </div>

                <div className="space-y-4">
                  <div>
                    <Label className="text-xs uppercase tracking-wider mb-2">Cliente</Label>
                    <Select value={selectedCustomer || 'none'} onValueChange={(val) => setSelectedCustomer(val === 'none' ? null : val)}>
                      <SelectTrigger className="font-mono" data-testid="customer-select">
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-border">
                        <SelectItem value="none">Cliente avulso</SelectItem>
                        {customers.map(customer => (
                          <SelectItem key={customer.id} value={customer.id}>{customer.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {!selectedCustomer && (
                      <Input
                        placeholder="Nome do cliente"
                        value={customerName}
                        onChange={(e) => setCustomerName(e.target.value)}
                        className="mt-2 font-mono"
                        data-testid="customer-name-input"
                      />
                    )}
                  </div>

                  <div>
                    <Label className="text-xs uppercase tracking-wider mb-2">Pagamento</Label>
                    <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                      <SelectTrigger className="font-mono" data-testid="payment-method-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-border">
                        <SelectItem value="dinheiro">Dinheiro</SelectItem>
                        <SelectItem value="pix">PIX</SelectItem>
                        <SelectItem value="credito">Fiado</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-xs uppercase tracking-wider mb-2">Tipo</Label>
                    <Select value={saleType} onValueChange={setSaleType}>
                      <SelectTrigger className="font-mono" data-testid="sale-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-border">
                        <SelectItem value="individual">Individual</SelectItem>
                        <SelectItem value="comanda">Comanda</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <Button
                    onClick={handleCompleteSale}
                    disabled={loading || cart.length === 0}
                    className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-bold uppercase h-12"
                    data-testid="complete-sale-button"
                  >
                    {loading ? 'Processando...' : 'Finalizar Venda'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Sales;
